import { useState } from 'react';
import { api, type ShipTo, type ShipToAnswer, type ShipToInput, type ShipToOnFile } from './api';
import { useLoad } from './lib';

// A camp's delivery address, shared by the Onboarding and Ship-to consoles. Every purchase order the
// camp places ships to it (commerce copies it onto each PO), and commerce's checkout refuses a camp
// with none. Its source is either staff (recorded here or at onboarding, with who and when) or the
// camp's own order history (the shipping address on its most recent order) — a camp onboarded here
// has no history, so it has only what staff record.

export interface AddressDraft { company: string; street: string; city: string; region: string; postcode: string; countryId: string; telephone: string }
export const BLANK_ADDRESS: AddressDraft = { company: '', street: '', city: '', region: '', postcode: '', countryId: 'US', telephone: '' };

// What a purchase order's address needs — commerce migration 023's CHECKs and parseShipToInput.
const REQUIRED: { key: keyof AddressDraft; label: string }[] = [
  { key: 'street', label: 'street' }, { key: 'city', label: 'city' }, { key: 'region', label: 'state / region' }, { key: 'postcode', label: 'ZIP / postcode' },
];

/** The required fields still blank, by label ([] when the address can be saved). */
export const missingFields = (d: AddressDraft) => REQUIRED.filter((f) => d[f.key].trim() === '').map((f) => f.label);

/** The draft as commerce takes it: trimmed, blank optional fields left out. */
export function toShipToInput(d: AddressDraft): ShipToInput {
  const opt = (v: string) => v.trim() || undefined;
  return {
    company: opt(d.company), street: d.street.trim(), city: d.city.trim(), region: d.region.trim(), postcode: d.postcode.trim(),
    countryId: d.countryId, telephone: opt(d.telephone),
  };
}

/** Start an edit from the recorded address, else from the one the order history supplies. */
export function draftFrom(a: ShipToOnFile | ShipTo | null): AddressDraft {
  if (!a) return BLANK_ADDRESS;
  return {
    company: a.company ?? '', street: a.street, city: a.city ?? '', region: a.region ?? '', postcode: a.postcode ?? '',
    countryId: a.countryId ?? 'US', telephone: a.telephone ?? '',
  };
}

/** One line for a table cell: first street line, city, region postcode. */
export const shipToOneLine = (s: ShipTo) =>
  [s.street.split('\n')[0], s.city, [s.region, s.postcode].filter(Boolean).join(' ')].filter(Boolean).join(', ');

/** 'Sep 23, 2026, 4:32 PM ET' — pinned to en-US and Eastern (the facility zone), whoever is looking. */
const when = (iso: string) =>
  `${new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' })} ET`;

export function AddressFields({ value, onChange, campName }: { value: AddressDraft; onChange: (d: AddressDraft) => void; campName?: string }) {
  const bind = (key: keyof AddressDraft) => ({
    value: value[key],
    onChange: (e: { target: { value: string } }) => onChange({ ...value, [key]: e.target.value }),
  });
  return (
    <div className="addr-grid">
      <label className="field" title="Left blank, purchase orders carry the camp's name"><span>Company (optional)</span>
        <input className="search" maxLength={255} placeholder={campName || 'Camp Wildwood'} {...bind('company')} />
      </label>
      <label className="field addr-street"><span>Street *</span>
        <textarea className="search" rows={2} maxLength={500} placeholder={'12 Lake Rd\nSuite 110'} {...bind('street')} />
      </label>
      <label className="field"><span>City *</span><input className="search" maxLength={255} {...bind('city')} /></label>
      <label className="field"><span>State / region *</span><input className="search" maxLength={255} placeholder="NY" {...bind('region')} /></label>
      <label className="field"><span>ZIP / postcode *</span><input className="search" maxLength={20} {...bind('postcode')} /></label>
      <label className="field"><span>Country</span>
        <select className="sel" {...bind('countryId')}>
          <option value="US">United States</option>
          <option value="CA">Canada</option>
        </select>
      </label>
      <label className="field"><span>Telephone</span><input className="search" maxLength={50} {...bind('telephone')} /></label>
    </div>
  );
}

/** The address as the camp's purchase orders print it: with no company, the camp's name (commerce poShipTo). */
export function ShipToLines({ s, campName }: { s: ShipTo | ShipToOnFile; campName?: string | null }) {
  const place = [s.city, [s.region, s.postcode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const lines = [s.company ?? campName, ...s.street.split('\n'), place, s.countryId && s.countryId !== 'US' ? s.countryId : null, s.telephone ? `T: ${s.telephone}` : null]
    .filter((l): l is string => !!l && l.trim() !== '');
  return <address className="addr-lines">{lines.map((l, i) => <span key={i}>{l}</span>)}</address>;
}

/**
 * View a camp's effective delivery address — where it came from, and who recorded it when — and, for
 * admin/ops, record a new one. Recording replaces the address on file; there is no remove.
 */
export function ShipToEditor({ orgId, name, canEdit, onSaved }: {
  orgId: number; name?: string | null; canEdit: boolean; onSaved?: (a: ShipToAnswer) => void;
}) {
  const { data, err, reload } = useLoad(() => api.commerceShipTo(orgId), [orgId]);
  const [draft, setDraft] = useState<AddressDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (err) return <p className="msg bad">⚠ {err}</p>;
  if (!data) return <p className="loading">Loading the delivery address…</p>;
  const { shipTo, onFile } = data;
  const missing = draft ? missingFields(draft) : [];

  async function save() {
    if (!draft) return;
    setBusy(true); setMsg(null);
    try {
      const saved = await api.commerceSaveShipTo(orgId, toShipToInput(draft));
      setDraft(null);
      setMsg('✓ Saved — the camp\'s next purchase order ships here.');
      reload();
      onSaved?.(saved);
    } catch (e) {
      setMsg(`⚠ ${(e as Error).message}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="shipto">
      <div className="shipto-head">
        <strong>Delivery address{name ? ` — ${name}` : ''}</strong>
        {shipTo?.source === 'on_file' && <span className="pill shipto-src on-file">Recorded by staff</span>}
        {shipTo?.source === 'history' && <span className="pill shipto-src history">Inferred from order history</span>}
        {!shipTo && <span className="pill shipto-src none">None</span>}
      </div>
      {shipTo ? <ShipToLines s={shipTo} campName={name} /> : (
        <p className="msg bad">No delivery address: this camp's buyers can fill a cart, but every checkout is refused until one is recorded.</p>
      )}
      {shipTo?.source === 'on_file' && onFile && (
        <p className="muted shipto-audit">Last set by <strong>{onFile.updatedBy ?? 'unknown'}</strong> · {when(onFile.updatedAt)}</p>
      )}
      {shipTo?.source === 'history' && (
        <p className="muted shipto-audit">The shipping address on this camp's most recent order. Nobody has recorded one; recording one makes it explicit, and every later order ships to it instead.</p>
      )}
      {msg && <p className={`msg ${msg.startsWith('⚠') ? 'bad' : 'good'}`}>{msg}</p>}
      {canEdit && !draft && (
        <div className="actions">
          <button className="btn-sm" onClick={() => { setMsg(null); setDraft(draftFrom(onFile ?? shipTo)); }}>
            {onFile ? 'Edit address' : shipTo ? 'Record this address…' : 'Add address'}
          </button>
        </div>
      )}
      {draft && (
        <div className="shipto-form">
          <AddressFields value={draft} onChange={setDraft} campName={name ?? undefined} />
          <div className="actions">
            <button className="btn-sm" disabled={busy || missing.length > 0} onClick={save}>{busy ? 'Saving…' : 'Save address'}</button>
            <button className="btn-sm ghost" disabled={busy} onClick={() => setDraft(null)}>Cancel</button>
            {missing.length > 0 && <span className="muted">Needs {missing.join(', ')}.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
